/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";

import { COMPACT_VIEWPORT } from "../../theme";

const useStyles = makeStyles((theme) => ({
  titleContainer: {
    overflow: "hidden",
  },
  actions: {
    position: "absolute",
    top: 0,
    right: 0,
  },
  finePrint: {
    display: "block",
    marginBottom: 3,
  },
}));

interface IProps {
  extra: string;
  subtitle: string;
  title: string;
  actions?: React.ReactElement;
  finePrint?: string;
  className?: string;
}

export const TitleHero = ({
  extra,
  subtitle,
  title,
  actions,
  finePrint,
  className = " ",
}: IProps) => {
  const classes = useStyles();

  return (
    <Stack
      className={`marapp-qa-titlehero ${className}`}
      direction="row"
      sx={{ justifyContent: "space-between" }}
    >
      <Stack>
        <Typography
          variant="subtitle1"
          color="textPrimary"
          gutterBottom={true}
          sx={{ [COMPACT_VIEWPORT]: { fontSize: "0.9rem" } }}
        >
          {subtitle} | {extra}
        </Typography>

        <Grid container={true} alignItems="flex-end" wrap="nowrap">
          <Grid item={true} xs={true} className={classes.titleContainer}>
            <Typography
              component="h2"
              variant="h4"
              color="textPrimary"
              sx={{
                wordWrap: "anywhere",
                [COMPACT_VIEWPORT]: { fontSize: "1.3rem" },
              }}
            >
              {title}
            </Typography>
          </Grid>

          {!!finePrint && (
            <Grid item={true} xs={false}>
              <Typography
                variant="caption"
                color="textSecondary"
                className={classes.finePrint}
              >
                {finePrint}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Stack>

      {!!actions && <Box>{actions}</Box>}
    </Stack>
  );
};
